import { type FC, useContext, useState, useSyncExternalStore } from "react";
import {
  Code2,
  Download,
  FileText,
  Globe,
  Image,
  Loader2,
  Workflow,
} from "lucide-react";
import { artifactStore } from "../../adapters/artifactStore";
import { ArtifactLoaderContext } from "../../contexts/ArtifactLoaderContext";
import {
  ARTIFACT_TYPE_EXTENSIONS,
  ARTIFACT_TYPE_LABELS,
  type ArtifactType,
} from "../../types/artifact";

interface ArtifactReferenceCardProps {
  artifactId: string;
  title: string;
  type: string;
}

const TYPE_ICONS: Record<string, FC<{ size: number; className?: string }>> = {
  "text/markdown": FileText,
  "application/code": Code2,
  "text/html": Globe,
  "image/svg+xml": Image,
  "application/vnd.mermaid": Workflow,
};

export const ArtifactReferenceCard: FC<ArtifactReferenceCardProps> = ({
  artifactId,
  title,
  type,
}) => {
  const loaderContext = useContext(ArtifactLoaderContext);
  const client = loaderContext?.client;

  const artifact = useSyncExternalStore(
    (cb) => artifactStore.subscribe(cb),
    () => artifactStore.getArtifact(artifactId),
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const Icon = TYPE_ICONS[type] ?? FileText;
  const typeLabel = ARTIFACT_TYPE_LABELS[type as ArtifactType] ?? type;
  const isGenerating = !artifact || artifact.status === "streaming";

  const handleOpen = () => {
    artifactStore.setActiveArtifact(artifactId);
  };

  const canDownload = !isGenerating && artifact?.chatId && client;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!artifact?.chatId || !client) return;
    const ext = ARTIFACT_TYPE_EXTENSIONS[type as ArtifactType] ?? ".txt";
    setIsDownloading(true);
    try {
      await client.downloadArtifactContent(
        artifact.chatId,
        artifact.id,
        `${title || "artifact"}${ext}`,
      );
    } catch {
      // non-critical; user can retry
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => e.key === "Enter" && handleOpen()}
      className="cuadra-flex cuadra-w-full cuadra-max-w-lg cuadra-items-center cuadra-gap-3 cuadra-px-4 cuadra-py-3 cuadra-my-2 cuadra-rounded-xl cuadra-border cuadra-border-border cuadra-cursor-pointer hover:cuadra-opacity-90 cuadra-transition-colors"
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--cuadra-foreground) 7%, var(--cuadra-background))",
      }}
      aria-label={`Open artifact: ${title || "Untitled"}`}
    >
      {/* Icon — spinner while generating, type icon once ready */}
      <div className="cuadra-flex-shrink-0 cuadra-w-10 cuadra-h-10 cuadra-rounded-lg cuadra-flex cuadra-items-center cuadra-justify-center cuadra-text-muted-foreground">
        {isGenerating ? (
          <Loader2 size={20} className="cuadra-animate-spin" />
        ) : (
          <Icon size={22} />
        )}
      </div>

      {/* Title + type */}
      <div className="cuadra-flex-1 cuadra-min-w-0">
        <span
          className={`cuadra-block cuadra-font-brand cuadra-text-sm cuadra-font-normal cuadra-truncate ${isGenerating ? "cuadra-text-muted-foreground" : "cuadra-text-foreground"}`}
        >
          {title || "Untitled artifact"}
        </span>
        <span className="cuadra-text-xs cuadra-text-muted-foreground">
          {isGenerating ? "Creating…" : typeLabel}
        </span>
      </div>

      {/* Download — uses auth-aware fetch, not a bare link */}
      {canDownload && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="cuadra-flex-shrink-0 cuadra-p-1.5 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors disabled:cuadra-opacity-50"
          aria-label={`Download ${title || "artifact"}`}
        >
          {isDownloading ? (
            <Loader2 size={15} className="cuadra-animate-spin" />
          ) : (
            <Download size={15} />
          )}
        </button>
      )}
    </div>
  );
};
